import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {
  IGetPermissionsByResourceRequestInterfaces,
} from '@toolify/server/src/controllers/PermissionController/validators/GetPermissionsByResource/types/IGetPermissionsByResourceRequestInterfaces'
import {
  IGetMembersByResourceRequestInterfaces,
} from '@toolify/server/src/controllers/PermissionController/validators/GetMembersByResource/types/IGetMembersByResourceRequestInterfaces'
import {
  IUpdatePermissionsByResourceRequestInterfaces,
} from '@toolify/server/src/controllers/PermissionController/validators/UpdatePermissionsByResource/types/IUpdatePermissionsByResourceRequestInterfaces'
import {
  IGetUserPermissionsByResourceRequestInterfaces,
} from '@toolify/server/src/controllers/PermissionController/validators/GetUserPermissionsByResource/types/IGetUserPermissionsByResourceRequestInterfaces'
import {
  IGetUserPermissionsByResourcesRequestInterfaces,
} from '@toolify/server/src/controllers/PermissionController/validators/GetUserPermissionsByResources/types/IGetUserPermissionsByResourcesRequestInterfaces'
import {mapUserResponseInstance} from '@toolify/client/src/modules/api/singletons/mapUserResponseInstance'
import {
  IGetEntitiesByResourceRequestInterfaces,
} from '@toolify/server/src/controllers/PermissionController/validators/GetEntitiesByResource/types/IGetEntitiesByResourceRequestInterfaces'
import {PermissionEntityType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/PermissionEntityType'
import {IUserResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {createKeyedRequestCache} from '../keyedRequestCache'

const MEMBERS_CACHE_TTL_MS = 30 * 1000
const membersCacheByResource = createKeyedRequestCache<IGetMembersByResourceRequestInterfaces['response']>({ttlMs: MEMBERS_CACHE_TTL_MS})

export const usePermissionApiStore = defineStore('permissionApi', {
  state: () => ({
    isPublicByInheritanceByResourceKey: {} as Record<string, boolean>,
  }),
  actions: {
    async getPermissionsByResource(
      resourceType: ResourceType,
      resourceId: string,
    ): Promise<IGetPermissionsByResourceRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const response = await apiRequestStore.get<IGetPermissionsByResourceRequestInterfaces>(`/permissions/${resourceType}/${resourceId}`)
      this.isPublicByInheritanceByResourceKey[`${resourceType}:${resourceId}`] = response.isPublicByInheritance
      return response
    },

    async getUserPermissionMapByResource(
      resourceType: ResourceType,
      resourceId: string,
    ): Promise<IGetUserPermissionsByResourceRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.get<IGetUserPermissionsByResourceRequestInterfaces>(`/me/permissions/${resourceType}/${resourceId}`)
    },

    async getUserPermissionsByResources(
      resources: Array<{resourceType: ResourceType, resourceId: string}>,
    ): Promise<IGetUserPermissionsByResourcesRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IGetUserPermissionsByResourcesRequestInterfaces>('/me/permissions', {
        data: {
          resources,
        },
      }, {retryOnNetworkError: true})
    },

    async getMembersByResource(
      resourceType: ResourceType,
      resourceId: string,
    ): Promise<IGetMembersByResourceRequestInterfaces['response']> {
      return membersCacheByResource.get(`${resourceType}/${resourceId}`, async () => {
        const apiRequestStore = useApiRequestStore()
        const {users} = await apiRequestStore.get<IGetMembersByResourceRequestInterfaces>(`/members/${resourceType}/${resourceId}`)
        return {
          users: users.map(user => {
            return mapUserResponseInstance(user)
          }),
        }
      })
    },

    async putPermissionsByResource(
      resourceType: ResourceType,
      resourceId: string,
      simplePermissionItems,
    ): Promise<IUpdatePermissionsByResourceRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.put<IUpdatePermissionsByResourceRequestInterfaces>(`/permissions/${resourceType}/${resourceId}`, {
        data: {
          permissions: simplePermissionItems,
        },
      })
    },

    async getEntitiesByResource(
      resourceType: ResourceType,
      resourceId: string,
      entityTypes?: PermissionEntityType[],
      withMemberIds?: boolean,
    ): Promise<IGetEntitiesByResourceRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {entities} = await apiRequestStore.get<IGetEntitiesByResourceRequestInterfaces>(`/entities/${resourceType}/${resourceId}`, {
        params: {
          entityTypes,
          withMemberIds,
        },
      })
      
      return {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        entities: entities.map(entity => {
          if (entity.entityType === PermissionEntityType.User) {
            return {
              ...entity,
              payload: (mapUserResponseInstance(entity.payload as IToJson<IUserResponseInstance>)) as IUserResponseInstance,
            }
          }
          return entity
        }),
      }
    },
  },
})
